// Loading screen controller. The DOM is in index.html; this toggles state
// and (when called via applyBranding) replaces the placeholder logo + tagline
// with whatever the active project declares in rooms.json `branding`.

import type { BrandingConfig } from '../app/types';

export class LoadingScreen {
  private readonly root: HTMLElement;
  private readonly status: HTMLElement;
  private readonly bar: HTMLElement;
  private readonly logo: HTMLElement;
  private readonly tagline: HTMLElement;

  constructor() {
    this.root = requireEl('loading-screen');
    this.status = requireEl('loading-status');
    this.bar = requireEl('loading-bar-fill');
    this.logo = requireEl('loading-logo');
    this.tagline = requireEl('loading-tagline');
  }

  show(): void {
    this.root.classList.remove('is-hidden');
  }

  hide(): void {
    this.root.classList.add('is-hidden');
  }

  setStatus(text: string): void {
    this.status.textContent = text;
  }

  setProgress(fraction: number): void {
    const pct = Math.max(0, Math.min(1, fraction)) * 100;
    this.bar.style.width = `${pct.toFixed(1)}%`;
  }

  /**
   * Surface a terminal, human-readable error on the loading screen instead of
   * leaving it to spin forever (or throwing into the void). `hint` populates
   * the tagline line with a remediation step. Used for unrecoverable, non-
   * transient conditions — e.g. missing WebGL (see webglSupport.ts + main.ts),
   * where the old "recarregue a página" message was actively misleading.
   */
  setError(message: string, hint?: string): void {
    // Hide the progress bar — there's no progress to make.
    const barTrack = this.bar.parentElement ?? this.bar;
    barTrack.style.display = 'none';
    this.status.textContent = message;
    this.status.style.color = '#ff6b6b';
    if (hint !== undefined) {
      this.tagline.textContent = hint;
    }
  }

  /**
   * Replace the loading-screen logo + tagline with the project's branding.
   * Called from App.start() as soon as rooms.json is parsed, before the
   * panorama assets begin loading — gives the operator's brand the first
   * ~1-2 seconds of screen real estate every cold load. Idempotent.
   *
   * Behavior:
   *   - `branding.logoUrl` set → swap the wordmark for `<img>` with the
   *     project name as `alt`. On 404 falls back to the text wordmark.
   *   - No logoUrl, name present → use `name` as a text wordmark.
   *   - Tagline = `branding.subtitle ?? existing default`.
   */
  applyBranding(branding: BrandingConfig | undefined): void {
    if (!branding) return;
    this.logo.innerHTML = '';
    if (branding.logoUrl) {
      const img = document.createElement('img');
      img.src = branding.logoUrl;
      img.alt = branding.name || 'Logo';
      img.className = 'loading-screen__logo-img';
      img.addEventListener('error', () => {
        // Logo 404 → fall back to text wordmark only.
        img.remove();
        if (!this.logo.querySelector('.loading-screen__logo-name')) {
          this.logo.innerHTML = `<span>${escapeHtml(branding.name || 'imersivo3D')}</span>`;
        }
      });
      this.logo.appendChild(img);
    }
    if (branding.name) {
      const name = document.createElement('div');
      name.className = branding.logoUrl ? 'loading-screen__logo-name' : '';
      // When there's no logo image, render the name as the gold-accent wordmark
      // (matches the legacy "imersivo3D" style); when there IS a logo above it,
      // render the name as a plain subhead instead.
      if (branding.logoUrl) {
        name.textContent = branding.name;
      } else {
        name.innerHTML = `<span>${escapeHtml(branding.name)}</span>`;
      }
      this.logo.appendChild(name);
    }
    if (branding.subtitle) {
      this.tagline.textContent = branding.subtitle;
    }
  }
}

function requireEl(id: string): HTMLElement {
  const el = document.getElementById(id);
  if (!el) throw new Error(`[loadingScreen] missing #${id}`);
  return el;
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
